/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Calendar, MapPin, Tag, Search, ChevronLeft, Square, AlertCircle } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../components/config/firebase';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import project1 from "../Assets/project1.png";
import margolisImg from "../Assets/margolis.png";
import vainonaImg from "../Assets/Vainona.png";

const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 9000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, images.length]);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <div 
      className="relative h-64 group" 
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`Project image ${currentIndex + 1}`}
          className="absolute w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        />
      </AnimatePresence>
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white w-4' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
      
      <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handlePrevious}
          className="p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={handleNext}
          className="p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

const fallbackProjects = [
  {
    id: 'dhliwayo-mansion',
    title: 'Mansion for Mr Dhliwayo in Borrowdale',
    description: 'An opulent mansion featuring cutting-edge architectural design and luxurious interiors, crafted to reflect elegance and comfort in the prestigious Borrowdale neighborhood.',
    images: [project1],
    category: 'Residential',
    completionDate: '2023',
    location: 'Borrowdale, Harare',
    size: '800',
  },
  {
    id: 'margolis-mall',
    title: "Stephen Margolis' Shopping Mall",
    description: 'A dynamic and expansive shopping mall offering a blend of modern retail spaces, entertainment hubs, and sustainable design to create a top-tier shopping destination.',
    images: [margolisImg],
    category: 'Commercial',
    completionDate: '2023',
    location: 'Harare CBD',
    size: '2500',
  },
  {
    id: 'vainona-clusters',
    title: 'Vainona Clusters',
    description: 'A thoughtfully designed residential development offering a blend of contemporary homes and community-driven living, transforming Vainona into a vibrant, harmonious neighborhood.',
    images: [vainonaImg],
    category: 'Residential',
    completionDate: '2023',
    location: 'Vainona, Harare',
    size: '1200',
  },
];

export const Projects = ({title}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  
  // Add animation variants for filter buttons
  const filterButtonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 }
  };

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const projectsCollection = collection(db, 'projects');
        const projectSnapshot = await getDocs(projectsCollection);
        const projectList = projectSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        if (projectList.length > 0) {
          const projectsWithImages = await Promise.all(
            projectList.map(async (project) => {
              if (project.imageRefs) {
                try {
                  const imageUrls = await Promise.all(
                    project.imageRefs.map(async (imageRef) => {
                      const imageUrl = await getDownloadURL(ref(storage, imageRef));
                      return imageUrl;
                    })
                  );
                  return { ...project, images: imageUrls };
                } catch {
                  return project;
                }
              }
              return project;
            })
          );
          setProjects(projectsWithImages);
        } else {
          setProjects(fallbackProjects);
        }
      } catch (err) {
        console.warn("Using fallback projects:", err.message);
        setProjects(fallbackProjects);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filterProjects = (category) => {
    setFilter(category);
  };

  const filteredProjects = projects
    .filter(project => filter === 'all' || project.category === filter)
    .filter(project => 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="relative">
          <div className="w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
          <div className="w-16 h-16 border-t-4 border-blue-300 border-solid rounded-full animate-spin absolute top-0 left-0" 
               style={{ animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  const categories = ['all', ...new Set(projects.map(p => p.category))];

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/20 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Our Projects Portfolio
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            Discover our innovative architectural designs and successful project implementations
          </motion.p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="sticky top-0 z-30 bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search Bar */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-800/50" size={20} />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50 transition-all duration-300"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-3 justify-center md:justify-end">
              {categories.map((category) => (
                <motion.button
                  key={category}
                  variants={filterButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  onClick={() => filterProjects(category)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    filter === category
                      ? 'bg-blue-800 text-white shadow-lg'
                      : 'bg-gray-100 text-blue-800 hover:bg-gray-200'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-64 overflow-hidden">
                  <ImageCarousel 
                    images={project.images || []}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-800/90 via-blue-800/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-6 group-hover:translate-y-0 transition-transform duration-300">
                      <Link 
                        to={`/projects/${project.id}`}
                        className="inline-flex items-center gap-2 bg-yellow-400 text-blue-800 font-semibold py-3 px-6 rounded-xl hover:bg-yellow-300 transition-colors"
                      >
                        View Details
                        <ChevronRight size={18} />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-2 group-hover:text-yellow-400 transition-colors">
                    {project.title}
                  </h3>
                  <div className="h-0.5 w-12 bg-yellow-400 group-hover:w-full transition-all duration-300 mb-4"></div>
                  
                  <p className="text-gray-600 mb-6 line-clamp-3">{project.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-800" />
                      <span>{project.completionDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-800" />
                      <span>{project.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-blue-800" />
                      <span>{project.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Square className="w-4 h-4 text-blue-800" />
                      <span>{project.size} sqm</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Projects;


